import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import { usePrivateLayout } from '~/layouts/private/providers/privateLayout';

import { useAuth } from '~/common/providers/auth';
import { ISidebarItem } from '~/common/interfaces/ISidebarItem';
import { useCookies } from '~/common/hooks/use-cookies';
import { UserSubscriptionTypes } from '~/common/enums/UserSubscriptionTypes';
import { sidebar } from '~/common/constants/sidebar';
import { cookies } from '~/common/constants/cookies';

import { SidebarSubmenu } from './Submenu';
import { SidebarMenuContainer, SidebarMenuNavIndicator } from './styles';
import { SidebarNav } from './Nav';

export const SidebarMenu: FC = () => {
  const { user: { subscription } = {}, isWalletBetaTester } = useAuth();
  const { asPath } = useRouter();
  const { isMobile } = usePrivateLayout();
  const { getItem, setItem } = useCookies();
  const [dynamicSidebar, setDynamicSidebar] = useState<ISidebarItem[]>(sidebar);
  const [render, setRender] = useState<boolean>(false);

  const topIndicatorPosition = useMemo(() => {
    if (!render) {
      return 0;
    }

    if (asPath.includes('/favorites')) {
      return 30;
    }

    const toAlign = 7.5;
    const split = asPath.split('/').filter(Boolean);
    if (split.length > 1) {
      const [groupKey, navKey] = split;
      const group = dynamicSidebar.find((where) => where.key === groupKey);
      if (group && group.open) {
        const element = document.getElementById(navKey);
        const rect = element?.getBoundingClientRect();
        const returning = rect?.top + toAlign;
        setItem(cookies.CURRENT_SIDEBAR_POS, String(returning));
        return returning;
      }
    }

    const [key] = split;
    const element = document.getElementById(key);

    const rect = element?.getBoundingClientRect();
    const returning = rect?.top + toAlign;

    setItem(cookies.CURRENT_SIDEBAR_POS, String(returning));
    return returning;
  }, [asPath, render, setItem, dynamicSidebar]);

  const refflect = useCallback((refflected: ISidebarItem[]) => {
    setDynamicSidebar(refflected);
  }, []);

  useEffect(() => {
    setRender(true);
  }, []);

  const sidebarWithPermissionsAdjusted = useMemo(() => {
    if (!sidebar || sidebar.length === 0) {
      return [];
    }

    if (subscription === UserSubscriptionTypes.admin) {
      return sidebar;
    }

    // Gate temporário: beta testers da Carteira mantêm os itens de wallet (externalHref), mas
    // não os demais itens adminOnly. Ver WalletBetaTesters no backend / WALLET_BETA_TESTERS.md.
    const withoutAdminOnly = sidebar.filter(
      (where) =>
        (!where.adminOnly && where.isVisible !== false) ||
        (isWalletBetaTester && !!where.externalHref)
    );

    const returning =
      !subscription || subscription === UserSubscriptionTypes.free
        ? withoutAdminOnly
        : withoutAdminOnly.filter((where) => where.key !== 'subscription');

    return returning
      .filter((where) => !where.externalHref || isWalletBetaTester)
      .map((where) =>
        where.children
          ? {
              ...where,
              children: where.children.filter(
                (child) => child.isVisible !== false
              ),
            }
          : where
      );
  }, [subscription, isWalletBetaTester]);

  return (
    <SidebarMenuContainer>
      <SidebarMenuNavIndicator
        style={{
          top: `${
            topIndicatorPosition ||
            Number(getItem(cookies.CURRENT_SIDEBAR_POS) || 0)
          }px`,
        }}
      />
      {sidebarWithPermissionsAdjusted.map((menu, index) => {
        if (menu.isVisible === false || (menu.key === 'moneyflow' && isMobile))
          return null;

        return (menu.children || []).length > 0 ? (
          <SidebarSubmenu
            key={menu.key}
            submenu={{
              ...menu,
              children: menu.children.filter(
                (child) => child.isVisible !== false
              ),
            }}
            index={index}
            refflect={refflect}
          />
        ) : (
          <SidebarNav key={menu.key} nav={menu} index={index} />
        );
      })}
    </SidebarMenuContainer>
  );
};
